import { useEffect, useState } from 'react';
import { IonButton, IonIcon, IonInput, IonItem, IonSpinner } from '@ionic/react';
import { checkmarkCircle, peopleOutline } from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { connections as connectionsApi } from '../lib/connections';
import { getDeviceName, setDeviceName } from '../lib/device';
import { enableDevicePush } from '../lib/push';
import type { Connection } from '../lib/types';

interface Props {
  code: string;
  onDone: () => void;
  onError: (message: string) => void;
}

type Phase = 'loading' | 'invalid' | 'confirm' | 'connecting' | 'done';

/** Bestätigen-Bildschirm für einen Kontakt-Einladungslink (`?connect=<code>`). */
export function ConnectAccept({ code, onDone, onError }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [inviterName, setInviterName] = useState('');
  const [myName, setMyName] = useState(() => getDeviceName());
  const [connection, setConnection] = useState<Connection | null>(null);
  const [pushHint, setPushHint] = useState<string | null>(null);

  useEffect(() => {
    connectionsApi
      .preview(code)
      .then(({ inviterName: name }) => {
        setInviterName(name);
        setPhase('confirm');
      })
      .catch(() => setPhase('invalid'));
  }, [code]);

  async function accept() {
    if (!myName.trim()) {
      onError('Bitte trage zuerst deinen Namen ein - der Kontakt sieht ihn.');
      return;
    }
    setDeviceName(myName);
    setPhase('connecting');
    try {
      const { connection: created } = await connectionsApi.accept(code);
      setConnection(created);
      // Direkt hier fragen - das ist der einzige Moment ohne aktive Fahrt,
      // in dem der Browser die nötige Nutzer-Geste (dieser Tipp) hat.
      const result = await enableDevicePush();
      if (result.state !== 'granted') setPushHint(result.message ?? null);
      setPhase('done');
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Verbindung fehlgeschlagen.');
      setPhase('confirm');
    }
  }

  if (phase === 'loading') {
    return (
      <Page title="Kontakt verbinden">
        <div className="r2f-row" style={{ justifyContent: 'center', padding: 40 }}>
          <IonSpinner name="dots" />
        </div>
      </Page>
    );
  }

  if (phase === 'invalid') {
    return (
      <Page title="Kontakt verbinden">
        <Card>
          <h2>Code ungültig</h2>
          <p>
            Dieser Einladungscode wurde schon benutzt, ist abgelaufen (nach 24 Stunden) oder
            vertippt. Lass dir am besten einen neuen geben.
          </p>
        </Card>
        <IonButton expand="block" onClick={onDone}>
          Zur Startseite
        </IonButton>
      </Page>
    );
  }

  if (phase === 'done') {
    return (
      <Page title="Verbunden">
        <Card>
          <div className="r2f-status">
            <IonIcon icon={checkmarkCircle} color="success" style={{ fontSize: 48 }} />
            <h2 style={{ marginTop: 12 }}>Mit {connection?.label ?? inviterName} verbunden</h2>
            <p>Künftige Fahrten lassen sich jetzt mit einem Tipp starten.</p>
          </div>
          {pushHint && (
            <div className="r2f-hint r2f-hint--warn" style={{ marginTop: 12 }}>
              {pushHint}
            </div>
          )}
        </Card>
        <IonButton expand="block" onClick={onDone}>
          Weiter
        </IonButton>
      </Page>
    );
  }

  return (
    <Page title="Kontakt verbinden">
      <Card>
        <div className="r2f-status">
          <IonIcon icon={peopleOutline} color="primary" style={{ fontSize: 48 }} />
          <h2 style={{ marginTop: 12 }}>Mit {inviterName} verbinden?</h2>
          <p>
            Ihr könnt danach jederzeit mit einem Tipp eine Fahrt starten - ohne Code. Dafür
            braucht es einmalig deine Erlaubnis für Benachrichtigungen.
          </p>
        </div>
        <IonItem lines="none" style={{ marginTop: 8 }}>
          <IonInput
            label="Dein Name"
            labelPlacement="stacked"
            placeholder={`So sieht dich ${inviterName} in Zukunft`}
            maxlength={40}
            value={myName}
            onIonInput={(event) => setMyName(event.detail.value ?? '')}
          />
        </IonItem>
      </Card>
      <IonButton expand="block" size="large" onClick={accept} disabled={phase === 'connecting'}>
        {phase === 'connecting' ? <IonSpinner name="dots" /> : 'Verbinden'}
      </IonButton>
      <IonButton expand="block" fill="clear" color="medium" onClick={onDone}>
        Abbrechen
      </IonButton>
    </Page>
  );
}
